/** For more information on implementation, see the documentation at:
 * https://tanstack.com/query/latest/docs/framework/react/guides/advanced-ssr */

// Since QueryClientProvider relies on useContext under the hood, we have to put 'use client' on top
import { isTRPCClientError } from "@trpc/client";
import { isServer, QueryClient } from "@tanstack/react-query";
import { isPlannerSessionNotSeededTRPCError } from "@portal/lib/trpc/helpers";

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (isTRPCClientError(error)) {
            if (
              error.data?.httpStatus === 404 ||
              error.data?.code === "NOT_FOUND"
            ) {
              return false;
            }

            if (isPlannerSessionNotSeededTRPCError(error)) {
              return false;
            }
          }
          return failureCount < 3;
        },
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) {
      browserQueryClient = makeQueryClient();
      window.__TANSTACK_QUERY_CLIENT__ = browserQueryClient;
    }
    return browserQueryClient;
  }
}
